import { isIosCompanionApp } from "@/lib/native/runtime";
import { Link, useNavigate } from "react-router-dom";
import { Button, buttonProps } from "@a2z/ui";
import type { SellerAuthView } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { formatCurrency } from "@/lib/formatting/currency";
import { getSellerPath } from "@/lib/routing/portal-urls";
import { useStoreSubscriptionQuery } from "@/shared/subscriptions/services/useStoreSubscriptionQuery";
import { usePageTitleChrome } from "@/shared/layout/usePageTitleChrome";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { MobileScreenHeader } from "@/shared/components/MobileScreenHeader";
import { AccessCard, LockIcon, SparkleIcon, useDocumentTitle } from "./AccessCard";
import { planFeatureFor } from "./plan-features";
import { previousPortalPath } from "./portal-history";
import {
  canAccess,
  denialReason,
  isStoreOwner,
  sellerLanding,
  STORE_ROUTES,
  type AccessRule,
} from "./store-access";

/**
 * What a seller sees on a route their store cannot open, INSIDE the shell —
 * the nav and bottom bar stay, so nothing about it reads as "you were thrown
 * out". Three honest cases:
 *
 * - the store's plan lacks the feature → name it, say what it does, and point
 *   at "Your plan" (one primary action), with "Go back" as the escape;
 * - the member's role lacks the permission → say so and who can grant it (no
 *   Premium pitch: buying Premium would not help);
 * - the address is not a page at all → say that.
 *
 * Never a logout button: nothing here is fixed by signing out.
 */
export function FeatureLocked({
  seller,
  rule,
  locale,
  storeSlug,
  inShell = false,
}: {
  seller: SellerAuthView | undefined;
  rule: AccessRule | undefined;
  locale: string;
  storeSlug: string;
  /** Inside the AppShell: also name the page in the desktop top bar. */
  inShell?: boolean;
}) {
  const t = useTranslations();
  const navigate = useNavigate();
  const reason = denialReason(seller, rule);
  const feature = reason === "capability" ? planFeatureFor(rule?.capability) : undefined;
  const planHref = getSellerPath(locale, storeSlug, "/plan");
  // A member (not the owner) cannot buy Premium: no plan link, no price.
  const owner = isStoreOwner(seller);
  const companion = isIosCompanionApp();
  const canOpenPlan = !companion && owner && canAccess(seller, STORE_ROUTES.plan);
  const landingHref = getSellerPath(locale, storeSlug, `/${sellerLanding(seller)}`);
  // Back to where the seller came from when that was a page of this portal —
  // a client-side navigation (router index > 0), or the page this tab showed
  // before a full load of this address (portal-history) — else the landing
  // page (the dashboard for an owner).
  const goBack = () => {
    const index = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (index > 0) {
      navigate(-1);
      return;
    }
    const previous = previousPortalPath(window.location.pathname + window.location.search);
    const sameStore = previous?.startsWith(`${getSellerPath(locale, storeSlug)}/`) ?? false;
    navigate(sameStore && previous ? previous : landingHref, { replace: true });
  };

  const featureName = feature ? t(feature.label) : t(keys.plan.locked.genericFeature);
  const title =
    reason === "capability"
      ? featureName
      : reason === "permission"
        ? t(keys.plan.locked.permissionTitle)
        : t(keys.plan.locked.missingTitle);
  useDocumentTitle(`${title} - Trioty`);
  const isMobile = useIsMobile();
  // On a phone inside the shell the locked page gets the standard page frame
  // — the large-title header with the bell — like every other screen. It
  // used to be a bare card under the top edge with no title and no bell.
  const mobileFrame = inShell && isMobile;
  const topBar = inShell ? (
    <>
      <ShellTitle title={title} />
      {mobileFrame ? (
        <div className="-mx-4 -mt-1 mb-3 sm:-mx-6">
          <MobileScreenHeader
            theme="light"
            surface="var(--shell)"
            title={title}
            ariaLabel={title}
            heading={
              <h1 className="m-0 text-[23px] font-bold leading-tight tracking-[-0.025em] text-[color:var(--text-primary)]">
                {title}
              </h1>
            }
          />
        </div>
      ) : null}
    </>
  ) : null;
  const cardTitleAs = mobileFrame ? "h2" : "h1";
  const frameClass = mobileFrame
    ? "px-4 pb-6 pt-1 sm:px-6"
    : "px-4 py-6 sm:px-6 lg:py-12";

  const back = (
    <Button type="button" variant="ghost" size="lg" onClick={goBack}>
      {t(keys.plan.locked.back)}
    </Button>
  );

  if (reason !== "capability") {
    return (
      <div className={frameClass}>
        {topBar}
        <AccessCard
          icon={<LockIcon />}
          title={title}
          titleAs={cardTitleAs}
          actions={back}
        >
          <p className="m-0">
            {reason === "permission"
              ? t(keys.plan.locked.permissionBody)
              : t(keys.plan.locked.missingBody)}
          </p>
        </AccessCard>
      </div>
    );
  }

  return (
    <div className={frameClass}>
      {topBar}
      <AccessCard
        icon={<SparkleIcon />}
        eyebrow={companion ? undefined : t(keys.plan.locked.eyebrow)}
        title={featureName}
        titleAs={cardTitleAs}
        actions={
          <>
            {canOpenPlan ? (
              <Link
                to={planHref}
                {...buttonProps({ variant: "primary", size: "lg" })}
              >
                {t(keys.plan.locked.cta)}
              </Link>
            ) : null}
            {back}
          </>
        }
      >
        <p className="m-0">
          {companion ? t(keys.iosCompanion.unavailable) : feature?.benefit ? t(feature.benefit) : t(keys.plan.locked.genericBenefit)}
        </p>
        {companion ? null : !owner ? (
          <p className="m-0 text-[13px] text-[color:var(--text-muted)]">
            {t(keys.plan.locked.memberAskOwner)}
          </p>
        ) : seller?.access?.tier === "FREE" ? (
          <FreePlanLine locale={locale} storeSlug={storeSlug} />
        ) : null}
      </AccessCard>
    </div>
  );
}

/** The desktop top bar would otherwise fall back to "Dashboard". */
function ShellTitle({ title }: { title: string }) {
  usePageTitleChrome({ icon: <SparkleIcon />, title });
  return null;
}

/** "You're on Free" plus the Premium price, when the backend has one to show. */
function FreePlanLine({ locale, storeSlug }: { locale: string; storeSlug: string }) {
  const t = useTranslations();
  const { data } = useStoreSubscriptionQuery({ storeSlug });
  const premium = data?.planOffer?.premium ?? null;
  return (
    <p className="m-0 text-[13px] text-[color:var(--text-muted)]">
      {t(keys.plan.locked.currentFree)}
      {premium ? (
        <>
          {" "}
          {t(keys.wallet.tierPremiumPrice, {
            amount: formatCurrency(premium.amount, locale, premium.currency),
            days: premium.periodDays,
          })}
        </>
      ) : null}
    </p>
  );
}
